// src/components/Auth/ProtectedRoute.js
import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import { useMockAuth0, isE2ETestMode } from './MockAuth0Provider';
import { notify } from '../../utils/notifications';
import PageTransition from '../PageTransition/PageTransition';
import PageSkeleton from '../LoadingSkeleton/PageSkeleton';

// Custom hook that switches between real and mock auth
const useAuth = () => {
    const realAuth = useAuth0();
    const mockAuth = useMockAuth0();
    return isE2ETestMode() ? mockAuth : realAuth;
};

const sanitizeNamespace = (namespace) => {
    if (!namespace) {
        return '';
    }
    return namespace.endsWith('/') ? namespace.slice(0, -1) : namespace;
};

const buildClaimKeys = (namespace, claim) => {
    const sanitized = sanitizeNamespace(namespace);
    return sanitized
        ? [`${sanitized}/${claim}`, claim]
        : [claim];
};

const extractClaimValues = (sources, namespace, claim) => {
    if (!Array.isArray(sources)) {
        return [];
    }

    const keys = buildClaimKeys(namespace, claim);

    for (const source of sources) {
        if (!source) {
            continue;
        }

        for (const key of keys) {
            const value = source[key];

            if (Array.isArray(value)) {
                return value;
            }

            if (typeof value === 'string' && value.trim() !== '') {
                return [value];
            }
        }

        // Support Auth0's authorization extension structure
        if (claim === 'roles') {
            const extensionRoles = source?.app_metadata?.authorization?.roles;
            if (Array.isArray(extensionRoles) && extensionRoles.length > 0) {
                return extensionRoles;
            }
        }

        if (claim === 'permissions') {
            const extensionPermissions = source?.app_metadata?.authorization?.permissions || source?.permissions;
            if (Array.isArray(extensionPermissions) && extensionPermissions.length > 0) {
                return extensionPermissions;
            }
        }
    }

    return [];
};

const ProtectedRoute = ({ children, requiredRoles = [], requiredPermissions = [] }) => {
    const {
        isAuthenticated,
        loginWithRedirect,
        getAccessTokenSilently,
        getIdTokenClaims,
        user,
        isLoading
    } = useAuth();
    const location = useLocation();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const authNamespace = useMemo(() => {
        const envNamespace = process.env.REACT_APP_AUTH0_NAMESPACE;
        if (envNamespace && envNamespace.trim() !== '') {
            return envNamespace;
        }

        const domain = process.env.REACT_APP_AUTH0_DOMAIN;
        if (domain && domain.trim() !== '') {
            return `https://${domain}`;
        }

        return '';
    }, []);

    useEffect(() => {
        let isMounted = true;

        const checkAuth = async () => {
            if (!isMounted) {
                return;
            }

            if (!isAuthenticated) {
                setIsAuthorized(false);
                setIsChecking(false);
                return;
            }

            try {
                // Get fresh token
                await getAccessTokenSilently();

                // Fetch latest ID token claims to ensure we have role/permission metadata,
                // especially after a hard refresh when the in-memory user object may not be hydrated yet.
                let idTokenClaims = null;
                try {
                    idTokenClaims = await getIdTokenClaims();
                } catch (claimsError) {
                    console.warn('ProtectedRoute: unable to retrieve ID token claims', claimsError);
                }

                if (!user && !idTokenClaims) {
                    // Neither the Auth0 user object nor the ID token claims are available yet;
                    // keep waiting without forcing a redirect.
                    setIsChecking(true);
                    return;
                }

                const claimSources = [
                    idTokenClaims,
                    user,
                    user?.app_metadata,
                    user?.app_metadata?.authorization
                ];

                // Check roles and permissions
                const userRoles = extractClaimValues(claimSources, authNamespace, 'roles');
                const userPermissions = extractClaimValues(claimSources, authNamespace, 'permissions');

                // For now, allow access if user is authenticated and no specific roles/permissions required
                // Or if they have the required roles/permissions
                const hasRequiredRole = requiredRoles.length === 0 ||
                    requiredRoles.some(role => userRoles.includes(role));

                const hasRequiredPermission = requiredPermissions.length === 0 ||
                    requiredPermissions.some(permission => userPermissions.includes(permission));

                // If no requirements specified, allow authenticated users
                const hasAccess = (requiredRoles.length === 0 && requiredPermissions.length === 0) ||
                    (hasRequiredRole && hasRequiredPermission);

                if (isMounted) {
                    setIsAuthorized(hasAccess);
                }
            } catch (error) {
                console.error('Error checking authentication:', error);
                notify.error('Authentication error. Please try again.');
                if (isMounted) {
                    setIsAuthorized(false);
                }
            } finally {
                if (isMounted) {
                    setIsChecking(false);
                }
            }
        };

        if (!isLoading) {
            checkAuth();
        }

        // Set up token refresh interval
        const tokenRefreshInterval = setInterval(async () => {
            if (isAuthenticated) {
                try {
                    await getAccessTokenSilently({ ignoreCache: true });
                } catch (error) {
                    console.error('Error refreshing token:', error);
                    notify.error('Session expired. Please log in again.');
                    loginWithRedirect();
                }
            }
        }, 10 * 60 * 1000); // Refresh every 10 minutes

        return () => {
            isMounted = false;
            clearInterval(tokenRefreshInterval);
        };
    }, [
        authNamespace,
        getAccessTokenSilently,
        getIdTokenClaims,
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        requiredPermissions,
        requiredRoles,
        user
    ]);

    if (isLoading || isChecking) {
        return <PageSkeleton type={location.pathname.split('/')[1] || 'dashboard'} />;
    }

    if (isAuthenticated && !user) {
        // Auth0 is still hydrating the user profile after a refresh
        return <PageSkeleton type={location.pathname.split('/')[1] || 'dashboard'} />;
    }

    if (!isAuthenticated) {
        sessionStorage.setItem('intendedPath', location.pathname);
        loginWithRedirect();
        return null;
    }

    if (!isAuthorized) {
        notify.error('You do not have permission to access this page');
        return <Navigate to="/" />;
    }

    return (
        <PageTransition>
            {children}
        </PageTransition>
    );
};

export default ProtectedRoute;
